import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { EvaluationStandardDraft } from '../../types/evaluationPlan';
import {
  EMPTY_EVALUATION_LEVELS,
  type EvaluationLevelDisplayLabels,
  type EvaluationLevelStepCount,
  getActiveLevelKeys,
  getActiveLevelLabels,
  formatLevelHeadLabel,
} from '../../types/evaluationPlan';
import { sizeTextareaToContent } from '../todoNote/todoNoteRowShared';

function cloneStandard(standard: EvaluationStandardDraft): EvaluationStandardDraft {
  return { ...standard, ...EMPTY_EVALUATION_LEVELS, ...standard };
}

function achievementTextForForm(standard: EvaluationStandardDraft): string {
  const text = (standard.achievement_text ?? '').trim();
  if (text) return standard.achievement_text ?? '';
  const code = (standard.achievement_code ?? '').trim();
  if (!code) return '';
  return code;
}

function emptyDraft(categoryName: string): EvaluationStandardDraft {
  return {
    id: crypto.randomUUID(),
    category_name: categoryName,
    track_text: '',
    area_text: '',
    achievement_code: '',
    achievement_text: '',
    sort_order: 0,
    ...EMPTY_EVALUATION_LEVELS,
  };
}

const AutoGrowTextarea: React.FC<{
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  'aria-label'?: string;
}> = ({ value, onChange, autoFocus, 'aria-label': ariaLabel }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    sizeTextareaToContent(ref.current, 72);
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  return (
    <textarea
      ref={ref}
      className="cp-todo-note-plain cp-todo-note-plain-memo cp-evaluation-plan-standard-form-textarea"
      rows={1}
      value={value}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      onInput={(e) => {
        onChange(e.currentTarget.value);
        sizeTextareaToContent(e.currentTarget, 72);
      }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const EvaluationStandardFormModal: React.FC<{
  open: boolean;
  mode: 'create' | 'edit';
  initial: EvaluationStandardDraft | null;
  categoryName: string;
  levelStepCount: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  onClose: () => void;
  onSubmit: (standard: EvaluationStandardDraft) => void;
}> = ({ open, mode, initial, categoryName, levelStepCount, levelDisplayLabels, onClose, onSubmit }) => {
  const [draft, setDraft] = useState<EvaluationStandardDraft>(() =>
    initial ? cloneStandard(initial) : emptyDraft(categoryName)
  );

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const cloned = cloneStandard(initial);
      setDraft({
        ...cloned,
        achievement_text: achievementTextForForm(cloned),
        achievement_code: '',
      });
    } else {
      setDraft(emptyDraft(categoryName));
    }
  }, [open, initial, categoryName]);

  if (!open) return null;

  const patch = (patchValue: Partial<EvaluationStandardDraft>) => {
    setDraft((prev) => ({ ...prev, ...patchValue }));
  };

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-standard-form-modal"
        role="dialog"
        aria-modal="true"
        data-tutorial-ep="standard-form-modal"
        aria-labelledby="cp-evaluation-plan-standard-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-evaluation-plan-standard-form-title">
          {mode === 'create' ? '성취기준 등록' : '성취기준 수정'}
        </h3>
        <div className="cp-modal-body cp-evaluation-plan-form-body">
          <div className="cp-todo-note-editor-blocks cp-evaluation-plan-standard-form-row cp-evaluation-plan-standard-form-row--single">
            <div className="cp-todo-note-editor-block cp-evaluation-plan-standard-form-block--narrow">
              <div className="cp-todo-note-editor-block-head">편제</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={draft.track_text}
                  onChange={(v) => patch({ track_text: v })}
                  autoFocus
                  aria-label="편제"
                />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-standard-form-block--narrow">
              <div className="cp-todo-note-editor-block-head">영역</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea value={draft.area_text} onChange={(v) => patch({ area_text: v })} aria-label="영역" />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-standard-form-block--achievement">
              <div className="cp-todo-note-editor-block-head">성취기준</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={draft.achievement_text}
                  onChange={(v) => patch({ achievement_text: v })}
                  aria-label="성취기준"
                />
              </div>
            </div>
            {getActiveLevelKeys(levelStepCount).map((key, idx) => {
              const levelLabel = getActiveLevelLabels(levelStepCount, levelDisplayLabels)[idx];
              const headLabel = formatLevelHeadLabel(levelLabel);
              return (
              <div key={key} className="cp-todo-note-editor-block cp-evaluation-plan-standard-form-block--level">
                <div className="cp-todo-note-editor-block-head">{headLabel}</div>
                <div className="cp-todo-note-editor-block-body">
                  <AutoGrowTextarea
                    value={draft[key]}
                    onChange={(v) => patch({ [key]: v })}
                    aria-label={headLabel}
                  />
                </div>
              </div>
            );
            })}
          </div>
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            onClick={() => {
              onSubmit({
                ...draft,
                category_name: categoryName,
                achievement_code: '',
                achievement_text: draft.achievement_text ?? '',
              });
              onClose();
            }}
          >
            {mode === 'create' ? '등록' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationStandardFormModal;
